// API client with automatic token handling and refresh

import { API_BASE_URL } from "./config"
import { getTokens, refreshAccessToken, logout } from "./auth"

interface FetchOptions extends RequestInit {
  skipAuth?: boolean
}

/**
 * Enhanced fetch with automatic token injection and refresh
 */
export async function apiFetch(endpoint: string, options: FetchOptions = {}): Promise<Response> {
  const { skipAuth = false, ...fetchOptions } = options

  // Build full URL
  const url = endpoint.startsWith("http") ? endpoint : `${API_BASE_URL}${endpoint}`

  console.log("[v0] API request to:", url)

  // Get tokens
  const tokens = getTokens()

  // Add authorization header if not skipping auth and tokens exist
  if (!skipAuth && tokens?.access) {
    fetchOptions.headers = {
      ...fetchOptions.headers,
      Authorization: `Bearer ${tokens.access}`,
    }
  }

  fetchOptions.credentials = "include"

  // Make initial request
  let response = await fetch(url, fetchOptions)

  // If unauthorized and we have a refresh token, try to refresh
  if (response.status === 401 && !skipAuth && tokens?.refresh) {
    console.log("[v0] Access token expired, attempting refresh...")

    const newTokens = await refreshAccessToken()

    if (newTokens) {
      // Retry request with new token
      fetchOptions.headers = {
        ...fetchOptions.headers,
        Authorization: `Bearer ${newTokens.access}`,
      }
      response = await fetch(url, fetchOptions)
    } else {
      // Refresh failed, logout user
      console.log("[v0] Token refresh failed, logging out...")
      logout()
      throw new Error("Session expired. Please login again.")
    }
  }

  return response
}

/**
 * Convenience method for GET requests
 */
export async function apiGet<T>(endpoint: string, options?: FetchOptions): Promise<T> {
  const response = await apiFetch(endpoint, {
    ...options,
    method: "GET",
    cache: options?.cache || "default",
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Request failed" }))
    throw new Error(error.detail || `Request failed with status ${response.status}`)
  }

  return response.json()
}

/**
 * Convenience method for GET requests that return binary data (PDFs, images, etc.)
 */
export async function apiGetBlob(endpoint: string, options?: FetchOptions): Promise<Blob> {
  const response = await apiFetch(endpoint, {
    ...options,
    method: "GET",
    cache: options?.cache || "default",
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Request failed" }))
    throw new Error(error.detail || `Request failed with status ${response.status}`)
  }

  return response.blob()
}

/**
 * Convenience method for POST requests
 */
export async function apiPost<T>(endpoint: string, data?: any, options?: FetchOptions): Promise<T> {
  const response = await apiFetch(endpoint, {
    ...options,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
    body: data ? JSON.stringify(data) : undefined,
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Request failed" }))
    throw new Error(error.detail || `Request failed with status ${response.status}`)
  }

  return response.json()
}

/**
 * Convenience method for PUT requests
 */
export async function apiPut<T>(endpoint: string, data?: any, options?: FetchOptions): Promise<T> {
  const response = await apiFetch(endpoint, {
    ...options,
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
    body: data ? JSON.stringify(data) : undefined,
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Request failed" }))
    throw new Error(error.detail || `Request failed with status ${response.status}`)
  }

  return response.json()
}

/**
 * Convenience method for DELETE requests
 */
export async function apiDelete<T>(endpoint: string, options?: FetchOptions): Promise<T> {
  const response = await apiFetch(endpoint, { ...options, method: "DELETE" })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Request failed" }))
    throw new Error(error.detail || `Request failed with status ${response.status}`)
  }

  // Handle empty responses
  const text = await response.text()
  return text ? JSON.parse(text) : ({} as T)
}

/**
 * Convenience method for PATCH requests
 */
export async function apiPatch<T>(endpoint: string, data?: any, options?: FetchOptions): Promise<T> {
  const response = await apiFetch(endpoint, {
    ...options,
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
    body: data ? JSON.stringify(data) : undefined,
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Request failed" }))
    throw new Error(error.detail || `Request failed with status ${response.status}`)
  }

  return response.json()
}

/**
 * Upload file with multipart/form-data with extended timeout for long operations
 */
export async function apiUpload<T>(
  endpoint: string,
  formData: FormData,
  options?: FetchOptions & { timeout?: number },
): Promise<T> {
  const timeoutMs = options?.timeout || 300000 // 5 minutes default

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs)

  try {
    const response = await apiFetch(endpoint, {
      ...options,
      method: "POST",
      body: formData,
      signal: controller.signal,
      // Don't set Content-Type header - browser will set it with boundary
    })

    clearTimeout(timeoutId)

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({ detail: "Upload failed" }))
      const err = new Error(errorBody.detail || `Upload failed with status ${response.status}`)
      ;(err as any).body = errorBody
      throw err
    }

    return response.json()
  } catch (error) {
    clearTimeout(timeoutId)

    if (error instanceof Error && error.name === "AbortError") {
      throw new Error("Request timed out. The operation may still be processing in the background.")
    }

    throw error
  }
}



